import React, { useEffect, useMemo, useState } from 'react';
import { EditorContent, useEditor } from '@tiptap/react';
import { HighlightColor } from '@narrativebench/editor-core';
import { ChevronDown, ChevronLeft, ChevronRight, ChevronUp, FilePlus, Link2, RotateCcw, Undo2 } from 'lucide-react';

import { useLocale } from '../contexts/LocaleContext';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { API_BASE } from '../config';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  ButtonTooltip,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './ui/tooltip';
import { TextDiff, countInlineDiffChanges } from './TextDiff';
import { toBcp47Locale } from '../locales/locale';
import {
  buildVersionComparisonUrl,
  findNewerReadyVersionId,
  findOlderReadyVersionId,
  isVersionInspectable,
  summarizeWordDiff,
  type DocumentVersionListItem,
  type VersionInspectorMode,
} from '../utils/document-version-navigation';
import { copyTextToClipboard } from '../utils/copy-text-to-clipboard';
import { createStaticEditorExtensions } from '../utils/editor-extensions';

interface VersionPayload {
  id: string;
  label: string | null;
  createdAt: string;
  contentJson: unknown;
  plainText: string;
  canRestore?: boolean;
}

export type VersionInspectState = {
  mode: VersionInspectorMode;
  versionId: string | null;
  compareVersionId: string | null;
};

interface VersionInspectorProps {
  isOpen: boolean;
  onClose: () => void;
  documentId: string;
  versions: DocumentVersionListItem[];
  versionId: string | null;
  onVersionIdChange: (versionId: string) => void;
  initialMode?: VersionInspectorMode;
  initialCompareVersionId?: string | null;
  onInspectStateChange?: (state: VersionInspectState) => void;
  onRestore?: (versionId: string) => void;
  onCreateDocument?: (versionId: string) => void;
  restoring?: boolean;
  creatingDocument?: boolean;
  allowCompareSelection?: boolean;
  allowVersionNavigation?: boolean;
}

export const VersionInspector: React.FC<VersionInspectorProps> = ({
  isOpen,
  onClose,
  documentId,
  versions,
  versionId,
  onVersionIdChange,
  initialMode = 'changes',
  initialCompareVersionId = null,
  onInspectStateChange,
  onRestore,
  onCreateDocument,
  restoring = false,
  creatingDocument = false,
  allowCompareSelection = true,
  allowVersionNavigation = true,
}) => {
  const { t, locale } = useLocale();
  const { token } = useAuth();
  const { showToast, showErrorToast } = useToast();
  const [mode, setMode] = useState<VersionInspectorMode>(initialMode);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentVersion, setCurrentVersion] = useState<VersionPayload | null>(null);
  const [compareVersion, setCompareVersion] = useState<VersionPayload | null>(null);
  const [compareUnavailable, setCompareUnavailable] = useState(false);
  const [pinnedCompareVersionId, setPinnedCompareVersionId] = useState<string | null>(null);
  const [activeChangeIndex, setActiveChangeIndex] = useState(0);
  const [changeScrollRequestKey, setChangeScrollRequestKey] = useState(0);

  const olderVersionId = versionId ? findOlderReadyVersionId(versions, versionId) : null;
  const newerVersionId = versionId ? findNewerReadyVersionId(versions, versionId) : null;
  const selectedListVersion = versions.find((version) => version.id === versionId);
  const isMostRecent = versions[0]?.id === versionId;
  const canRestore = Boolean(
    currentVersion
    && !isMostRecent
    && selectedListVersion
    && selectedListVersion.status !== 'invalid'
    && selectedListVersion.status !== 'pending_migration'
    && selectedListVersion.canRestore !== false,
  );

  const compareOptions = useMemo(
    () => inspectableVersionsExcept(versions, versionId),
    [versions, versionId],
  );

  const lockedCompareVersionId = allowCompareSelection
    ? null
    : (
      initialCompareVersionId
      && initialCompareVersionId !== versionId
        ? initialCompareVersionId
        : null
    );

  const isComparePinned = allowCompareSelection
    && pinnedCompareVersionId !== null
    && pinnedCompareVersionId !== versionId
    && compareOptions.some((version) => version.id === pinnedCompareVersionId);

  const effectiveCompareVersionId = allowCompareSelection
    ? (isComparePinned ? pinnedCompareVersionId : olderVersionId)
    : lockedCompareVersionId;

  const editor = useEditor({
    extensions: createStaticEditorExtensions(HighlightColor),
    editable: false,
    immediatelyRender: false,
    editorProps: {
      attributes: {
        class: 'prose prose-sm sm:prose lg:prose-lg xl:prose-2xl mx-auto focus:outline-none min-h-[240px] p-4',
      },
    },
  });

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    setMode(initialMode);
  }, [isOpen, initialMode]);

  useEffect(() => {
    if (!allowCompareSelection) {
      return;
    }
    if (!isOpen) {
      setPinnedCompareVersionId(null);
      return;
    }
    if (!initialCompareVersionId || initialCompareVersionId === versionId) {
      setPinnedCompareVersionId(null);
      return;
    }
    if (!versions.some((version) => (
      version.id === initialCompareVersionId && isVersionInspectable(version)
    ))) {
      return;
    }
    const defaultOlder = versionId ? findOlderReadyVersionId(versions, versionId) : null;
    setPinnedCompareVersionId(
      initialCompareVersionId === defaultOlder ? null : initialCompareVersionId,
    );
  }, [allowCompareSelection, isOpen, initialCompareVersionId, versionId, versions.length]);

  useEffect(() => {
    if (!allowCompareSelection) {
      return;
    }
    if (
      pinnedCompareVersionId !== null
      && (pinnedCompareVersionId === versionId
        || !compareOptions.some((version) => version.id === pinnedCompareVersionId))
    ) {
      setPinnedCompareVersionId(null);
    }
  }, [allowCompareSelection, pinnedCompareVersionId, versionId, compareOptions]);

  useEffect(() => {
    if (!isOpen || !onInspectStateChange || !versionId) {
      return;
    }
    if (allowCompareSelection && versions.length === 0) {
      return;
    }
    onInspectStateChange({
      mode,
      versionId,
      compareVersionId: mode === 'changes' ? effectiveCompareVersionId : null,
    });
  }, [
    isOpen,
    mode,
    versionId,
    effectiveCompareVersionId,
    onInspectStateChange,
    allowCompareSelection,
    versions.length,
  ]);

  useEffect(() => {
    if (!isOpen || !versionId || !token) {
      return;
    }

    let cancelled = false;

    const fetchVersionPayload = async (id: string): Promise<VersionPayload> => {
      const response = await fetch(
        `${API_BASE}/api/documents/${documentId}/versions/${id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({})) as { error?: string };
        const error = new Error(
          response.status === 410
            ? t('versionHistory.unavailable')
            : (errorData.error || t('versionHistory.loadVersionError')),
        );
        throw error;
      }

      const version = await response.json();
      return {
        id: version.id,
        label: version.label,
        createdAt: version.createdAt,
        contentJson: version.contentJson,
        plainText: typeof version.plainText === 'string' ? version.plainText : '',
        canRestore: version.canRestore,
      };
    };

    const load = async (): Promise<void> => {
      try {
        setLoading(true);
        setError(null);
        setCompareUnavailable(false);

        const current = await fetchVersionPayload(versionId);
        if (cancelled) {
          return;
        }
        setCurrentVersion(current);

        if (!effectiveCompareVersionId) {
          setCompareVersion(null);
          return;
        }

        try {
          const compare = await fetchVersionPayload(effectiveCompareVersionId);
          if (!cancelled) {
            setCompareVersion(compare);
          }
        } catch (compareError) {
          if (!cancelled) {
            setCompareVersion(null);
            setCompareUnavailable(true);
            console.error('Error fetching compare version:', compareError);
          }
        }
      } catch (err) {
        console.error('Error fetching version:', err);
        if (!cancelled) {
          setCurrentVersion(null);
          setCompareVersion(null);
          setError(err instanceof Error ? err.message : t('versionHistory.loadVersionError'));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, [isOpen, versionId, effectiveCompareVersionId, documentId, token, t]);

  useEffect(() => {
    if (!editor || !isOpen || loading) {
      return;
    }

    if (!currentVersion) {
      editor.commands.setContent('');
      return;
    }

    if (!currentVersion.contentJson) {
      editor.commands.setContent('');
      return;
    }

    const applied = editor.commands.setContent(currentVersion.contentJson as any);
    if (!applied) {
      setError(t('versionHistory.loadVersionError'));
      editor.commands.setContent('');
    }
  }, [editor, isOpen, loading, currentVersion, t]);

  useEffect(() => {
    if (!isOpen && editor) {
      editor.commands.setContent('');
      setCurrentVersion(null);
      setCompareVersion(null);
      setError(null);
      setPinnedCompareVersionId(null);
    }
  }, [isOpen, editor]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') {
        return;
      }

      const target = event.target as HTMLElement | null;
      if (target) {
        const tag = target.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') {
          return;
        }
        if (target.isContentEditable) {
          return;
        }
        if (target.closest('[role="listbox"]') || target.closest('[data-radix-select-content]')) {
          return;
        }
      }

      const nextId = event.key === 'ArrowLeft' ? olderVersionId : newerVersionId;
      if (!nextId) {
        return;
      }

      event.preventDefault();
      onVersionIdChange(nextId);
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen, olderVersionId, newerVersionId, onVersionIdChange]);

  const changeCount = useMemo(() => {
    if (!currentVersion || !compareVersion) {
      return 0;
    }
    return countInlineDiffChanges(compareVersion.plainText, currentVersion.plainText);
  }, [currentVersion, compareVersion]);

  useEffect(() => {
    setActiveChangeIndex(0);
    setChangeScrollRequestKey(0);
  }, [versionId, currentVersion?.id, compareVersion?.id]);

  useEffect(() => {
    setActiveChangeIndex((current) => (
      changeCount === 0 ? 0 : Math.min(current, changeCount - 1)
    ));
  }, [changeCount]);

  const goToPreviousChange = (): void => {
    if (changeCount === 0) {
      return;
    }
    setActiveChangeIndex((current) => (current - 1 + changeCount) % changeCount);
    setChangeScrollRequestKey((current) => current + 1);
  };

  const goToNextChange = (): void => {
    if (changeCount === 0) {
      return;
    }
    setActiveChangeIndex((current) => (current + 1) % changeCount);
    setChangeScrollRequestKey((current) => current + 1);
  };

  const handleCopyComparisonUrl = (): void => {
    if (!versionId || !effectiveCompareVersionId) {
      return;
    }
    const comparisonUrl = buildVersionComparisonUrl(window.location.href, {
      versionId,
      compareVersionId: effectiveCompareVersionId,
    });
    void copyTextToClipboard(comparisonUrl).then((copied) => {
      if (copied) {
        showToast(t('link_copied'));
      } else {
        showErrorToast(t('versionHistory.copyComparisonUrlFailed'));
      }
    });
  };

  const formatDate = (dateString: string): string => {
    return new Intl.DateTimeFormat(toBcp47Locale(locale), {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(dateString));
  };

  const wordSummary = useMemo(() => {
    if (!currentVersion || !compareVersion) {
      return null;
    }
    return summarizeWordDiff(compareVersion.plainText, currentVersion.plainText);
  }, [currentVersion, compareVersion]);

  const versionTitle = currentVersion
    ? (currentVersion.label || t('versionHistory.automatic'))
    : t('versionHistory.title');

  const compareVersionLabel = compareVersion
    ? `${formatDate(compareVersion.createdAt)} · ${compareVersion.label || t('versionHistory.automatic')}`
    : null;
  const showCompareControls = mode === 'changes' && (
    allowCompareSelection
      ? compareOptions.length > 0
      : Boolean(effectiveCompareVersionId || compareVersion || compareUnavailable)
  );

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between pr-8">
            <DialogTitle>
              {versionTitle}
              {currentVersion && (
                <span className="text-sm font-normal text-muted-foreground ml-2">
                  {formatDate(currentVersion.createdAt)}
                </span>
              )}
            </DialogTitle>
            <div className="flex items-center gap-2">
              {allowVersionNavigation && (
                <>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => olderVersionId && onVersionIdChange(olderVersionId)}
                          disabled={!olderVersionId}
                          aria-label={t('versionHistory.olderVersion')}
                        >
                          <ChevronLeft className="h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>{t('versionHistory.olderVersion')}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => newerVersionId && onVersionIdChange(newerVersionId)}
                          disabled={!newerVersionId}
                          aria-label={t('versionHistory.newerVersion')}
                        >
                          <ChevronRight className="h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>{t('versionHistory.newerVersion')}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </>
              )}
              {onCreateDocument && versionId && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onCreateDocument(versionId)}
                        disabled={creatingDocument || loading || Boolean(error)}
                      >
                        <FilePlus className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>{t('versionHistory.createDocumentTooltip')}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
              {onRestore && versionId && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onRestore(versionId)}
                        disabled={!canRestore || restoring || loading || Boolean(error)}
                      >
                        <RotateCcw className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>{t('versionHistory.backToThisVersionTooltip')}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
          </div>
        </DialogHeader>

        <Tabs value={mode} onValueChange={(value) => setMode(value as VersionInspectorMode)}>
          <TabsList>
            <TabsTrigger value="changes">{t('versionHistory.changesMode')}</TabsTrigger>
            <TabsTrigger value="fullText">{t('versionHistory.fullTextMode')}</TabsTrigger>
          </TabsList>

          {showCompareControls && (
            <div className="mt-3 mb-1 flex items-center gap-2">
              <span className="text-sm text-muted-foreground whitespace-nowrap">
                {t('versionHistory.compareWith')}
              </span>
              {allowCompareSelection ? (
                <>
                  <Select
                    value={effectiveCompareVersionId ?? undefined}
                    onValueChange={setPinnedCompareVersionId}
                    disabled={loading || compareOptions.length === 0}
                  >
                    <SelectTrigger className="w-full sm:w-[320px]" aria-label={t('versionHistory.compareWith')}>
                      <SelectValue placeholder={t('versionHistory.compareWith')} />
                    </SelectTrigger>
                    <SelectContent className="z-[2010]">
                      {compareOptions.map((version) => (
                        <SelectItem key={version.id} value={version.id}>
                          {formatDate(version.createdAt)}
                          {' · '}
                          {version.label || t('versionHistory.automatic')}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {isComparePinned && (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setPinnedCompareVersionId(null)}
                            aria-label={t('versionHistory.resetCompareWith')}
                          >
                            <Undo2 className="h-4 w-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>{t('versionHistory.resetCompareWith')}</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                </>
              ) : (
                <span className="text-sm text-foreground">
                  {compareVersionLabel || (loading ? t('versionHistory.loading') : '—')}
                </span>
              )}
              {allowCompareSelection && versionId && effectiveCompareVersionId && (
                <TooltipProvider delayDuration={0}>
                  <ButtonTooltip tooltip={t('versionHistory.copyComparisonUrlTooltip')} side="bottom">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleCopyComparisonUrl}
                      aria-label={t('versionHistory.copyComparisonUrl')}
                    >
                      <Link2 className="h-4 w-4" />
                    </Button>
                  </ButtonTooltip>
                </TooltipProvider>
              )}
            </div>
          )}

          {loading ? (
            <div className="py-8 text-center">{t('versionHistory.loading')}</div>
          ) : error ? (
            <div className="py-8 text-center text-destructive">{error}</div>
          ) : (
            <>
              <TabsContent value="changes">
                {!effectiveCompareVersionId ? (
                  <div className="py-8 text-center text-muted-foreground">
                    {t('versionHistory.firstVersionMessage')}
                  </div>
                ) : compareUnavailable ? (
                  <div className="py-8 text-center text-muted-foreground">
                    {t('versionHistory.previousUnavailable')}
                  </div>
                ) : currentVersion && compareVersion && currentVersion.plainText === compareVersion.plainText ? (
                  <div className="py-8 text-center text-muted-foreground">
                    {t('versionHistory.noChanges')}
                  </div>
                ) : currentVersion && compareVersion ? (
                  <div>
                    <div className="flex items-center justify-between gap-3">
                      {wordSummary && (
                        <div className="text-sm text-muted-foreground">
                          {t('versionHistory.diffSummary')
                            .replace('{added}', String(wordSummary.added))
                            .replace('{removed}', String(wordSummary.removed))}
                        </div>
                      )}
                      {changeCount > 0 && (
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-muted-foreground whitespace-nowrap">
                            {t('versionHistory.changePosition')
                              .replace('{current}', String(activeChangeIndex + 1))
                              .replace('{total}', String(changeCount))}
                          </span>
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={goToPreviousChange}
                                  aria-label={t('versionHistory.previousChange')}
                                >
                                  <ChevronUp className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>{t('versionHistory.previousChange')}</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={goToNextChange}
                                  aria-label={t('versionHistory.nextChange')}
                                >
                                  <ChevronDown className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>{t('versionHistory.nextChange')}</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                      )}
                    </div>
                    <TextDiff
                      before={compareVersion.plainText}
                      after={currentVersion.plainText}
                      variant="inline"
                      className="mt-2"
                      activeChangeIndex={changeCount > 0 ? activeChangeIndex : null}
                      scrollRequestKey={changeScrollRequestKey}
                    />
                  </div>
                ) : null}
              </TabsContent>
              <TabsContent value="fullText" forceMount className="data-[state=inactive]:!hidden overflow-y-auto max-h-[60vh]">
                <EditorContent editor={editor} />
              </TabsContent>
            </>
          )}
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

function inspectableVersionsExcept(
  versions: DocumentVersionListItem[],
  currentId: string | null,
): DocumentVersionListItem[] {
  return versions.filter(
    (version) => isVersionInspectable(version) && version.id !== currentId,
  );
}
